import { currentLang } from "../../../utility/index";

export interface ParsedErrorInfo {
  title: string;
  message: string;
  sapError?: string;
  existingOrderId?: string;
  isDuplicateOrder?: boolean;
  advice?: string;
  rawError?: string;
}

/**
 * Parses any backend error, Axios error response, or detail object
 * into structured and localized error details.
 */
export const parseBackendError = (
  error: any,
  fallbackMessage?: string,
  customTitle?: string
): ParsedErrorInfo => {
  const isArabic = currentLang() === "Arabic";
  const defaultFallback = isArabic
    ? "حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى."
    : "An unexpected error occurred. Please try again.";

  let rawDetail: string = "";

  if (typeof error === "string") {
    rawDetail = error.trim();
  } else if (error) {
    // Check axios response or direct response data or detail object
    const data =
      error.response?.data !== undefined
        ? error.response.data
        : error.data !== undefined
        ? error.data
        : error;

    if (typeof data === "string") {
      rawDetail = data.trim();
    } else if (data && typeof data === "object") {
      if (typeof data.detail === "string") {
        rawDetail = data.detail.trim();
      } else if (data.detail && typeof data.detail === "object") {
        if (typeof data.detail.message === "string") {
          rawDetail = data.detail.message.trim();
        } else if (typeof data.detail.msg === "string") {
          rawDetail = data.detail.msg.trim();
        } else if (typeof data.detail.error === "string") {
          rawDetail = data.detail.error.trim();
        } else if (Array.isArray(data.detail)) {
          const msgs = data.detail
            .map((item: any) => {
              if (typeof item === "string") return item;
              if (item?.msg) {
                const loc = Array.isArray(item?.loc) ? item.loc.join(" -> ") : "";
                return loc ? `${loc}: ${item.msg}` : item.msg;
              }
              if (item?.message) return item.message;
              return null;
            })
            .filter(Boolean);
          if (msgs.length > 0) rawDetail = msgs.join("\n");
        }
      }

      if (!rawDetail && typeof data.message === "string") {
        rawDetail = data.message.trim();
      }
      if (!rawDetail && typeof data.error === "string") {
        rawDetail = data.error.trim();
      }
      if (!rawDetail && Array.isArray(data.errors) && data.errors.length > 0) {
        const msgs = data.errors
          .map((e: any) => (typeof e === "string" ? e : e?.message || e?.msg))
          .filter(Boolean);
        if (msgs.length > 0) rawDetail = msgs.join("\n");
      }
    }

    if (!rawDetail && typeof error.message === "string") {
      if (error.message === "Network Error") {
        rawDetail = isArabic
          ? "خطأ في الاتصال بالشبكة. يرجى التحقق من اتصالك بالإنترنت."
          : "Network Error. Please check your internet connection.";
      } else {
        rawDetail = error.message.trim();
      }
    }
  }

  if (!rawDetail) {
    rawDetail = fallbackMessage || defaultFallback;
  }

  // Parse SAP_ERROR or Order pattern from rawDetail
  // Example: "Failed to create Sales Order in SAP. SAP_ERROR : Order-0030011429, is already created for the reference"
  let title = customTitle || (isArabic ? "تنبيه" : "Error");
  let message = rawDetail;
  let sapError: string | undefined = undefined;
  let existingOrderId: string | undefined = undefined;
  let isDuplicateOrder = false;
  let advice: string | undefined = undefined;

  const sapMarkerRegex = /SAP_ERROR\s*:\s*/i;
  if (sapMarkerRegex.test(rawDetail)) {
    const parts = rawDetail.split(sapMarkerRegex);
    const mainPart = parts[0].replace(/[\s\.\-]+$/, "").trim();
    const sapPart = (parts[1] || "").trim();

    sapError = sapPart;

    if (
      /already created/i.test(sapPart) ||
      /already exists/i.test(sapPart) ||
      /duplicate/i.test(sapPart)
    ) {
      isDuplicateOrder = true;
      title = customTitle || mainPart || (isArabic ? "الطلب مسجل مسبقاً في SAP" : "Failed to create Sales Order in SAP");
      message = mainPart || (isArabic ? "فشل إنشاء أمر المبيعات في SAP" : "Failed to create Sales Order in SAP.");
      advice = isArabic
        ? "تم إنشاء هذا الطلب مسبقاً بنجاح في SAP."
        : "An order for this reference has already been created in SAP.";
    } else {
      title = customTitle || mainPart || (isArabic ? "خطأ في استجابة SAP" : "SAP Order Error");
      message = mainPart || (isArabic ? "فشل إنشاء طلب المبيعات في SAP" : "Failed to create Sales Order in SAP.");
    }

    // Extract Order reference e.g. "Order-0030011429" or "0030011429"
    const orderMatch = sapPart.match(/(?:Order[-:\s]*)(\d+|[A-Za-z0-9-]+)/i);
    if (orderMatch) {
      existingOrderId = orderMatch[0].trim().replace(/,$/, "");
    }
  } else if (/already created/i.test(rawDetail) || /already exists/i.test(rawDetail)) {
    isDuplicateOrder = true;
    title = customTitle || (isArabic ? "الطلب مسجل مسبقاً" : "Order Already Exists");
    advice = isArabic
      ? "يرجى مراجعة قائمة الطلبات السابقة لتجنب تكرار الطلب."
      : "Please check your existing orders to avoid duplicate submissions.";
    const orderMatch = rawDetail.match(/(?:Order[-:\s]*)(\d+|[A-Za-z0-9-]+)/i);
    if (orderMatch) {
      existingOrderId = orderMatch[0].trim().replace(/,$/, "");
    }
  }

  return {
    title,
    message,
    sapError,
    existingOrderId,
    isDuplicateOrder,
    advice,
    rawError: rawDetail,
  };
};

/**
 * Extracts a human-readable error message from backend API errors,
 * Axios error responses, or fallback text.
 */
export const getBackendErrorMessage = (
  error: any,
  fallbackMessage?: string
): string => {
  const parsed = parseBackendError(error, fallbackMessage);
  return parsed.message;
};
